import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr, inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

export default function ItemRegister() {
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [f, setF] = useState({ company: "all", category: "all", group: "all", date_from: "", date_to: "" });

  useEffect(() => {
    api.get("/companies").then(r => setCompanies(r.data)).catch(() => {});
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    api.get("/groups").then(r => setGroups(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const p = new URLSearchParams();
    if (f.date_from) p.set("date_from", f.date_from);
    if (f.date_to) p.set("date_to", f.date_to);
    const t = setTimeout(() => {
      api.get(`/register/summary?${p}`).then(r => setRows(r.data)).catch(e => toast.error(formatErr(e)));
    }, 200);
    return () => clearTimeout(t);
  }, [f.date_from, f.date_to]);

  const filtered = useMemo(() => rows.filter(r =>
    (f.company === "all" || r.company === f.company) &&
    (f.category === "all" || r.category === f.category) &&
    (f.group === "all" || r.group === f.group)
  ), [rows, f.company, f.category, f.group]);
  const pg = usePagination(filtered);

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><FileText className="w-3 h-3"/> Transactions</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Transaction Register</h1>
        <div className="text-xs text-muted-foreground mt-2">All items with their transaction counts — click an item to view its full transaction history.</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3 border border-border bg-card p-3 rounded-sm">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Company</Label>
          <Select value={f.company} onValueChange={(v) => setF(x => ({ ...x, company: v }))}>
            <SelectTrigger data-testid="reg-filter-company" className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All companies</SelectItem>{companies.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
          <Select value={f.category} onValueChange={(v) => setF(x => ({ ...x, category: v }))}>
            <SelectTrigger data-testid="reg-filter-category" className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Group</Label>
          <Select value={f.group} onValueChange={(v) => setF(x => ({ ...x, group: v }))}>
            <SelectTrigger data-testid="reg-filter-group" className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All groups</SelectItem>{groups.map(g => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">From Date</Label>
          <Input data-testid="reg-filter-from" type="date" value={f.date_from} onChange={(e) => setF(x => ({ ...x, date_from: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">To Date</Label>
          <Input data-testid="reg-filter-to" type="date" value={f.date_to} onChange={(e) => setF(x => ({ ...x, date_to: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="reg-list-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Item Name</th>
              <th className="text-left p-3">Part No</th>
              <th className="text-left p-3">HSN Code</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Unit</th>
              <th className="text-right p-3">Purchase Rate</th>
              <th className="text-right p-3">MRP</th>
              <th className="text-right p-3">Available Stock Qty</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground" data-testid="reg-list-empty">No items found</td></tr>}
            {pg.pageItems.map(r => (
              <tr key={r.item_id} data-testid={`reg-list-row-${r.item_id}`} className="border-t border-border hover:bg-secondary/40">
                <td className="p-3">
                  <Link to={`/item-register/${r.item_id}`} data-testid={`reg-item-link-${r.item_id}`} className="text-primary hover:underline font-medium">{r.item_name}</Link>
                </td>
                <td className="p-3 font-mono text-xs text-primary uppercase">{r.part_number || "-"}</td>
                <td className="p-3 font-mono text-xs text-muted-foreground">{r.hsn_code || "-"}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.category}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.unit}</td>
                <td className="p-3 text-right font-mono text-xs">{inr(r.per_unit_cost)}</td>
                <td className="p-3 text-right font-mono text-xs">{inr(r.mrp)}</td>
                <td className="p-3 text-right font-mono font-semibold">{r.stock}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
